import { useEffect, useState } from 'react';
import type { ScheduleItemView } from '@obraplan/core/application/contracts/schedule-items';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { formatBusinessDate } from '@/lib/format';
import { paraNumero } from '@/features/takeoff/formato';
import { formatDecimal, formatItemQuantity } from './schedule-items-model';

export interface ItemEdit {
  readonly plannedStart: string;
  readonly plannedEnd: string;
  readonly progressPct: number;
}

export interface EditItemDialogProps {
  /** O item aberto; `undefined` fecha o diálogo. */
  readonly item: ScheduleItemView | undefined;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSave: (item: ScheduleItemView, edit: ItemEdit) => void;
  readonly isPending: boolean;
  /** A recusa do servidor, para mostrar no formulário e não só num aviso que some. */
  readonly errorMessage: string | undefined;
}

/**
 * Reprogramar o item e lançar o avanço (`UpdateScheduleItem`, `schedule.edit`).
 *
 * Mexe só no **previsto** e no avanço: a linha de base nasceu com o item e não muda
 * (RN-322), e o diálogo a mostra de lado, sem campo, para ficar claro contra o que o
 * novo prazo é comparado.
 */
export function EditItemDialog({
  item,
  onOpenChange,
  onSave,
  isPending,
  errorMessage,
}: EditItemDialogProps) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [progress, setProgress] = useState('');

  // Cada item aberto recomeça do que ele tem hoje.
  useEffect(() => {
    if (item === undefined) return;
    setStart(item.plannedStart);
    setEnd(item.plannedEnd);
    setProgress(String(item.progressPct).replace('.', ','));
  }, [item]);

  const progressValue = paraNumero(progress);
  const startProblem = start === '' ? 'Informe o início.' : undefined;
  const endProblem =
    end === ''
      ? 'Informe o fim.'
      : start !== '' && start > end
        ? 'O início não pode ser depois do fim.'
        : undefined;
  // Com medição por peça o avanço sai do executado (RN-383): o campo é só leitura, e o
  // percentual gravado segue como está.
  const pelaMedicao = item?.progressFromExecution !== undefined;
  const progressProblem = pelaMedicao
    ? undefined
    : progressValue === undefined || progressValue < 0 || progressValue > 100
      ? 'O avanço vai de 0 a 100.'
      : undefined;
  const valid =
    startProblem === undefined && endProblem === undefined && progressProblem === undefined;

  const title =
    item === undefined
      ? 'Editar item'
      : `${item.disciplineName}${item.serviceName === null ? ' (frente inteira)' : ` · ${item.serviceName}`}`;

  return (
    <Dialog open={item !== undefined} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {item === undefined
              ? ''
              : `${item.towerName} · ${item.floorName}${item.segment === null ? '' : ` · ${item.segment}`}`}
          </DialogDescription>
        </DialogHeader>

        {item !== undefined && (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              const progressPct = pelaMedicao ? item.progressPct : progressValue;
              if (!valid || progressPct === undefined) return;
              onSave(item, { plannedStart: start, plannedEnd: end, progressPct });
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Início previsto" htmlFor="item-start" error={startProblem}>
                <Input
                  id="item-start"
                  type="date"
                  value={start}
                  onChange={(event) => setStart(event.target.value)}
                  aria-invalid={startProblem !== undefined}
                />
              </Field>
              <Field label="Fim previsto" htmlFor="item-end" error={endProblem}>
                <Input
                  id="item-end"
                  type="date"
                  value={end}
                  onChange={(event) => setEnd(event.target.value)}
                  aria-invalid={endProblem !== undefined}
                />
              </Field>
            </div>

            <Field
              label="Avanço (%)"
              htmlFor="item-progress"
              error={progressProblem}
              {...(item.progressFromExecution === undefined
                ? {}
                : {
                    hint: `Calculado pelo executado${
                      item.executedQuantity === undefined
                        ? ''
                        : ` (${formatItemQuantity({ quantity: item.executedQuantity, unit: item.unit }) ?? ''})`
                    } e não se digita: marque as peças no lançamento do dia.`,
                  })}
            >
              <Input
                id="item-progress"
                inputMode="decimal"
                value={
                  item.progressFromExecution === undefined
                    ? progress
                    : formatDecimal(item.progressFromExecution, 1)
                }
                disabled={pelaMedicao}
                onChange={(event) => setProgress(event.target.value)}
                aria-invalid={progressProblem !== undefined}
              />
            </Field>

            <p className="rounded-md bg-(--color-surface-muted) p-3 text-xs text-(--color-content-muted)">
              Linha de base: {formatBusinessDate(item.baselineStart)} a{' '}
              {formatBusinessDate(item.baselineEnd)}. Ela foi gravada na primeira importação e não
              muda — reprogramar mexe só no previsto.
            </p>

            {errorMessage !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {errorMessage}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={isPending || !valid}>
                {isPending ? 'Salvando…' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
